import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { FormInput } from "@/components/ui/FormInput";
import { useSetBreakGlassPassword } from "@/queries/identity/useSetBreakGlassPassword";

const passwordFieldSchema = z
  .string()
  .nonempty({ message: "Password is required" })
  .regex(/^(?=\S{6,100}$)(?=.*?\d)(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[^A-Za-z\s0-9])/, {
    message:
      "Password must be at least 6 characters, have 1 digit, 1 lowercase, 1 uppercase, and 1 special character",
  });

const breakGlassPasswordSchema = z
  .object({
    password: passwordFieldSchema,
    confirmPassword: z.string().nonempty({ message: "Please verify your password" }),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type BreakGlassPasswordForm = z.infer<typeof breakGlassPasswordSchema>;

interface BreakGlassPasswordSectionProps {
  embedded?: boolean;
  onSuccess?: () => void;
}

export const BreakGlassPasswordSection = ({
  embedded = false,
  onSuccess,
}: BreakGlassPasswordSectionProps) => {
  const form = useForm<BreakGlassPasswordForm>({
    resolver: zodResolver(breakGlassPasswordSchema),
    mode: "onChange",
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });
  const setPassword = useSetBreakGlassPassword();

  const onSubmit = (data: BreakGlassPasswordForm) => {
    setPassword.mutate(
      {
        password: data.password,
        confirm_password: data.confirmPassword,
      },
      {
        onSuccess: () => {
          form.reset();
          onSuccess?.();
        },
      },
    );
  };

  const containerClassName = embedded
    ? "flex flex-col gap-4"
    : "flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4";

  return (
    <div className={containerClassName}>
      {!embedded && (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-gray-900">Emergency access password</p>
          <p className="text-sm text-gray-500">
            You signed in with SSO and do not have a PromptLayer password yet. Set one here to sign
            in if Require SSO is enabled and your identity provider is unavailable.
          </p>
          <p className="text-xs font-medium text-amber-700">
            Required for organization Owners before turning on &ldquo;Require SSO&rdquo;.
          </p>
        </div>
      )}
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <FormInput
          {...form.register("password")}
          type="password"
          label="Password"
          placeholder="Password"
          autoComplete="new-password"
          error={form.formState.errors.password?.message}
        />
        <FormInput
          {...form.register("confirmPassword")}
          type="password"
          label="Confirm password"
          placeholder="Confirm password"
          autoComplete="new-password"
          error={form.formState.errors.confirmPassword?.message}
        />
        <div className="flex justify-start">
          <Button
            type="submit"
            variant="outline"
            disabled={!form.formState.isValid || setPassword.isLoading}
            className="w-fit"
          >
            Save password
          </Button>
        </div>
      </form>
    </div>
  );
};
